// frontend/src/features/campaign/utils/campaignReachabilityUtils.js
import { cleanPhoneNumber } from './outreachInterpolation';

/**
 * Computes channel reachability statistics for an imported cohort of leads.
 * Analyzes contact channel coverage across Email, WhatsApp, LinkedIn, and Phone Call.
 *
 * @param {Array} leads - Array of lead objects from formData.importedLeads
 * @returns {Object} Reachability statistics and coverage percentages
 */
export function computeCohortReachability(leads = []) {
  const safeLeads = Array.isArray(leads) ? leads : [];
  const total = safeLeads.length;

  if (total === 0) {
    return {
      total: 0,
      email: { count: 0, percentage: 0, missing: 0 },
      whatsApp: { count: 0, percentage: 0, missing: 0 },
      linkedIn: { count: 0, directCount: 0, percentage: 0, missingDirect: 0 },
      phoneCall: { count: 0, percentage: 0, missing: 0 },
      anyChannelCount: 0,
      missingAllCount: 0,
      hasLeads: false,
    };
  }

  let emailCount = 0;
  let phoneCount = 0;
  let linkedInDirectCount = 0;
  let anyChannelCount = 0;

  safeLeads.forEach((lead) => {
    const hasEmail = Boolean(lead?.email && String(lead.email).includes('@'));
    const hasPhone = Boolean(cleanPhoneNumber(lead?.phone));
    const hasLinkedInDirect = Boolean(
      lead?.linkedinUrl || lead?.linkedin_url || lead?.social?.linkedin
    );

    if (hasEmail) emailCount += 1;
    if (hasPhone) phoneCount += 1;
    if (hasLinkedInDirect) linkedInDirectCount += 1;

    if (hasEmail || hasPhone || hasLinkedInDirect) {
      anyChannelCount += 1;
    }
  });

  const calcPct = (count) => Math.round((count / total) * 100);

  return {
    total,
    email: {
      count: emailCount,
      percentage: calcPct(emailCount),
      missing: total - emailCount,
    },
    whatsApp: {
      count: phoneCount,
      percentage: calcPct(phoneCount),
      missing: total - phoneCount,
    },
    linkedIn: {
      count: total,
      directCount: linkedInDirectCount,
      percentage: calcPct(linkedInDirectCount),
      missingDirect: total - linkedInDirectCount,
    },
    phoneCall: {
      count: phoneCount,
      percentage: calcPct(phoneCount),
      missing: total - phoneCount,
    },
    anyChannelCount,
    missingAllCount: total - anyChannelCount,
    hasLeads: true,
  };
}

/**
 * Returns reachability metrics for a specific journey step's channel.
 *
 * @param {string} channel - Channel name (e.g. 'Email', 'LinkedIn', 'WhatsApp', 'Phone Call')
 * @param {Object} reachabilityStats - Result from computeCohortReachability
 * @returns {Object} Channel-specific reachability data
 */
export function getStepChannelCoverage(channel = 'Email', reachabilityStats = null) {
  if (!reachabilityStats || !reachabilityStats.hasLeads) {
    return {
      count: 0,
      total: 0,
      percentage: 0,
      missing: 0,
      hasCoverageGap: false,
      label: channel,
      icon: 'fas fa-envelope',
      themeClass: 'email',
    };
  }

  const raw = String(channel || '').toLowerCase();
  const total = reachabilityStats.total;

  if (raw.includes('phone') || raw.includes('call')) {
    const count = reachabilityStats.phoneCall.count;
    const pct = reachabilityStats.phoneCall.percentage;
    return {
      count,
      total,
      percentage: pct,
      missing: reachabilityStats.phoneCall.missing,
      hasCoverageGap: count < total,
      label: 'Sales Call',
      icon: 'fas fa-phone-alt',
      themeClass: 'phone',
    };
  }

  if (raw.includes('whatsapp')) {
    const count = reachabilityStats.whatsApp.count;
    const pct = reachabilityStats.whatsApp.percentage;
    return {
      count,
      total,
      percentage: pct,
      missing: reachabilityStats.whatsApp.missing,
      hasCoverageGap: count < total,
      label: 'WhatsApp',
      icon: 'fab fa-whatsapp',
      themeClass: 'whatsapp',
    };
  }

  if (raw.includes('linkedin')) {
    const directCount = reachabilityStats.linkedIn.directCount;
    const pct = reachabilityStats.linkedIn.percentage;
    return {
      count: directCount,
      total,
      percentage: pct,
      missing: reachabilityStats.linkedIn.missingDirect,
      hasCoverageGap: directCount < total,
      label: 'LinkedIn',
      icon: 'fab fa-linkedin-in',
      themeClass: 'linkedin',
    };
  }

  // Default: Email
  const count = reachabilityStats.email.count;
  const pct = reachabilityStats.email.percentage;
  return {
    count,
    total,
    percentage: pct,
    missing: reachabilityStats.email.missing,
    hasCoverageGap: count < total,
    label: 'Email',
    icon: 'fas fa-envelope',
    themeClass: 'email',
  };
}

/**
 * Checks whether a given lead possesses valid contact data for a specific channel.
 */
export function leadHasChannel(lead, channelName = '') {
  if (!lead) return false;
  const raw = String(channelName).toLowerCase().trim();
  if (raw.includes('phone') || raw.includes('call')) {
    return Boolean(cleanPhoneNumber(lead.phone || lead.mobile || lead.telephone));
  }
  if (raw.includes('whatsapp')) {
    return Boolean(cleanPhoneNumber(lead.phone || lead.mobile || lead.whatsappNumber));
  }
  if (raw.includes('linkedin')) {
    return Boolean(lead.linkedinUrl || lead.linkedin_url || lead.social?.linkedin || lead.linkedin);
  }
  if (raw.includes('telegram')) {
    return Boolean(lead.telegram || lead.telegramHandle || lead.telegram_handle);
  }
  // Default Email
  return Boolean(lead.email && String(lead.email).includes('@'));
}

/**
 * Retrieves the readable contact address/number/URL for a channel on a lead.
 */
export function getLeadChannelContactValue(lead, channelName = '') {
  if (!lead) return '';
  const raw = String(channelName).toLowerCase().trim();
  if (raw.includes('phone') || raw.includes('call')) {
    return cleanPhoneNumber(lead.phone || lead.mobile || lead.telephone) || lead.phone || '';
  }
  if (raw.includes('whatsapp')) {
    return cleanPhoneNumber(lead.phone || lead.mobile || lead.whatsappNumber) || lead.phone || '';
  }
  if (raw.includes('linkedin')) {
    return lead.linkedinUrl || lead.linkedin_url || lead.social?.linkedin || lead.linkedin || '';
  }
  if (raw.includes('telegram')) {
    return lead.telegram || lead.telegramHandle || lead.telegram_handle || '';
  }
  return lead.email || '';
}

/**
 * Returns all channels currently available for a lead from a list of candidate channels.
 */
export function getLeadAvailableChannels(
  lead,
  candidateChannels = ['Email', 'LinkedIn', 'WhatsApp', 'Phone Call']
) {
  return candidateChannels.filter((ch) => leadHasChannel(lead, ch));
}

/**
 * Computes the multi-channel breakdown for an outreach step across the cohort.
 * Identifies:
 * 1. Leads reachable via Primary Channel
 * 2. Leads that lack the primary channel and use a Fallback Channel
 * 3. Leads that cannot be contacted (missing all channels)
 *
 * @param {Array} leads - Full array of cohort leads
 * @param {string} primaryChannel - Channel selected for this step
 * @param {string} fallbackStrategy - Fallback option ('Auto', 'LinkedIn', 'WhatsApp', 'Phone Call', 'None')
 * @param {Array} activeChannels - Channels enabled in campaign
 * @param {Object} leadOverrides - User-specified manual channel overrides { [leadId]: channelName }
 * @returns {Object} Full breakdown object with arrays and counts
 */
export function computeStepLeadBreakdown(
  leads = [],
  primaryChannel = 'Email',
  fallbackStrategy = 'Auto',
  activeChannels = ['Email', 'LinkedIn', 'WhatsApp', 'Phone Call'],
  leadOverrides = {}
) {
  const safeLeads = Array.isArray(leads) ? leads : [];
  const primaryLeads = [];
  const fallbackLeads = [];
  const unreachableLeads = [];

  safeLeads.forEach((lead, index) => {
    const leadId = lead.id || lead._id || `lead-${index}`;
    const leadName =
      lead.name ||
      lead.fullName ||
      `${lead.firstName || ''} ${lead.lastName || ''}`.trim() ||
      `Prospect #${index + 1}`;
    const company = lead.company || lead.companyName || lead.organization || 'Enterprise Account';
    const title = lead.jobTitle || lead.title || lead.role || 'Decision Maker';

    const hasPrimary = leadHasChannel(lead, primaryChannel);
    const availableChannels = getLeadAvailableChannels(lead, activeChannels);
    const overrideChannel = leadOverrides ? leadOverrides[leadId] : null;

    // Handle manual lead channel override
    if (overrideChannel && leadHasChannel(lead, overrideChannel)) {
      if (overrideChannel === primaryChannel) {
        primaryLeads.push({
          leadId,
          lead,
          name: leadName,
          title,
          company,
          assignedChannel: primaryChannel,
          routeType: 'primary',
          contactValue: getLeadChannelContactValue(lead, primaryChannel),
          hasPrimary: true,
          availableChannels,
        });
      } else {
        fallbackLeads.push({
          leadId,
          lead,
          name: leadName,
          title,
          company,
          assignedChannel: overrideChannel,
          routeType: 'fallback',
          reason: `Manual override to ${overrideChannel}`,
          contactValue: getLeadChannelContactValue(lead, overrideChannel),
          hasPrimary,
          availableChannels,
        });
      }
      return;
    }

    if (hasPrimary) {
      primaryLeads.push({
        leadId,
        lead,
        name: leadName,
        title,
        company,
        assignedChannel: primaryChannel,
        routeType: 'primary',
        contactValue: getLeadChannelContactValue(lead, primaryChannel),
        hasPrimary: true,
        availableChannels,
      });
      return;
    }

    // Lead lacks primary channel; evaluate fallback strategy
    if (fallbackStrategy === 'None' || fallbackStrategy === 'none') {
      unreachableLeads.push({
        leadId,
        lead,
        name: leadName,
        title,
        company,
        assignedChannel: null,
        routeType: 'unreachable',
        reason: `Missing ${primaryChannel} (Fallback disabled)`,
        hasPrimary: false,
        availableChannels,
      });
      return;
    }

    // Determine fallback channel
    let resolvedFallback = null;
    if (fallbackStrategy && fallbackStrategy !== 'Auto' && fallbackStrategy !== 'auto') {
      if (leadHasChannel(lead, fallbackStrategy)) {
        resolvedFallback = fallbackStrategy;
      }
    } else {
      // Auto fallback: pick the first available alternative channel from active channels
      resolvedFallback = availableChannels.find((ch) => ch !== primaryChannel) || null;
    }

    if (resolvedFallback) {
      fallbackLeads.push({
        leadId,
        lead,
        name: leadName,
        title,
        company,
        assignedChannel: resolvedFallback,
        routeType: 'fallback',
        reason: `Missing ${primaryChannel} → routed to ${resolvedFallback}`,
        contactValue: getLeadChannelContactValue(lead, resolvedFallback),
        hasPrimary: false,
        availableChannels,
      });
    } else {
      unreachableLeads.push({
        leadId,
        lead,
        name: leadName,
        title,
        company,
        assignedChannel: null,
        routeType: 'unreachable',
        reason: `Missing ${primaryChannel} & no alternative channels recorded`,
        hasPrimary: false,
        availableChannels: [],
      });
    }
  });

  const total = safeLeads.length;
  const primaryCount = primaryLeads.length;
  const fallbackCount = fallbackLeads.length;
  const unreachableCount = unreachableLeads.length;
  const coveredCount = primaryCount + fallbackCount;
  const coveragePct = total > 0 ? Math.round((coveredCount / total) * 100) : 0;
  const primaryPct = total > 0 ? Math.round((primaryCount / total) * 100) : 0;

  return {
    total,
    primaryLeads,
    fallbackLeads,
    unreachableLeads,
    allLeads: [...primaryLeads, ...fallbackLeads, ...unreachableLeads],
    counts: {
      total,
      primaryCount,
      fallbackCount,
      unreachableCount,
      coveredCount,
      coveragePct,
      primaryPct,
    },
  };
}
